import {
  Button,
  cn,
  Icons,
  Input,
  Skeleton,
  Table,
} from "@win-reality/react-ui";

import { useNavMenu } from "@/components/ui/SideMenu";
import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { useComplexes } from "@/hooks/useComplexes";

import { useComplexesTableState } from "./Complexes.hooks";
import { useComplexesStore } from "./Complexes.store";
import { ComplexesItem } from "./ComplexesItem";
import { complexesColumns } from "./ComplexesTable.columns";

function Complexes() {
  const { isOpen, setIsOpen } = useNavMenu();

  const { complexesView: view, setComplexesView } = useComplexesStore();

  const { complexes, isLoading } = useComplexes({
    includeScheduledCounts: true,
  });
  const {
    filteredComplexes,
    sortingState,
    setSortingState,
    rawSearchValue,
    setSearchValue,
  } = useComplexesTableState(complexes, view);

  return (
    <div className={cn("flex flex-col gap-5 py-5 max-w-200 mx-auto")}>
      <div className="flex justify-between px-5">
        <div>
          <div className="shrink-0 md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
            >
              <Icons.Bars3BottomLeftOutline />
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </div>
        </div>

        <div className="flex gap-2">
          {view === "list" && (
            <Input
              placeholder="Search all"
              className="min-w-50"
              name="complex-search"
              id="complex-search"
              inputMode="text"
              type="text"
              value={rawSearchValue}
              isClearable={true}
              onClear={() => {
                setSearchValue("");
              }}
              iconRight={
                <Icons.MagnifyingGlass className="text-placeholder h-5 w-5" />
              }
              onChange={(e) => {
                if (e.target.value === "") {
                  setSearchValue("");
                  return;
                }

                setSearchValue(e.target.value);
              }}
            />
          )}

          <ViewToggleButton
            isActive={view === "grid"}
            icon={Icons.RectangleStackOutline}
            onClick={() => setComplexesView("grid")}
          />

          <ViewToggleButton
            isActive={view === "list"}
            icon={Icons.ListOutline}
            onClick={() => setComplexesView("list")}
          />
        </div>
      </div>

      {view === "grid" ? (
        <div className="px-5 flex flex-col gap-4">
          {isLoading ? (
            <>
              <Skeleton className="h-67.5 rounded" />
            </>
          ) : (
            <>
              {(complexes ?? []).map((complex) => (
                <ComplexesItem
                  key={complex.name}
                  name={complex.name}
                  fieldCount={complex.fields.length}
                  activeEvents={complex.activeEventsCount ?? 0}
                  scheduledEvents={complex.scheduledEventsCount}
                  imageUrl={complex.imageUrl}
                  fields={complex.fields}
                />
              ))}
            </>
          )}
        </div>
      ) : (
        <Table
          enableHorizontalScroll
          variant="infinite-scroll"
          hasMore={false}
          isFetching={isLoading}
          data={filteredComplexes ?? []}
          columns={complexesColumns}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          rowClassName="shadow-glow-tiny rounded"
          rowsWrapperClassName="gap-y-4"
          tableWrapperClassName="overflow-auto px-5 py-1"
          cellClassName="py-0 min-h-14"
        />
      )}
    </div>
  );
}

export { Complexes };
